import _round from 'lodash/round';
import { isCelsiusField, celsiusToFahrenheit, fahrenheitToCelsius, pascalToInchesOfWater, inchesOfWaterToPascal, PASCAL_DECIMALS, INCHES_OF_WATER_DECIMALS } from 'services/utils';

// A measured value, empty when the input is blank
type MeasuredValue = number | null | undefined;

// A unit the input can show instead of the API unit
type Unit = 'fahrenheit' | 'inchesOfWater';

interface UserPreferencesManagerLike {
    useFahrenheit: boolean;
    useInchesOfWater: boolean;
}

interface ScopeLike {
    $watchGroup(watchExpressions: Array<() => unknown>, listener: () => void): void;
}

interface AttributesLike {
    dtMeasure: string;
}

interface NgModelControllerLike {
    $formatters: Array<(value: MeasuredValue) => MeasuredValue>;
    $parsers: Array<(value: MeasuredValue) => MeasuredValue>;
    $processModelValue(): void;
}

/**
 * Tells an input what its model measures, e.g. temperature. The model keeps the
 * value in the unit the API uses, while the input shows it in the user's preferred
 * unit, e.g. °F for temperatures. What is measured is fixed or interpolated:
 *
 *   <input type="number" ng-model="$ctrl.celsius" dt-measure="temperature">
 *   <input type="number" ng-model="$ctrl.rule.trigger.range.lower" dt-measure="{{$ctrl.rule.trigger.field}}">
 */
export default function measureDirectiveConfig(UserPreferencesManager: UserPreferencesManagerLike) {
    function link($scope: ScopeLike, $element: unknown, $attrs: AttributesLike, ngModel: NgModelControllerLike): void {
        // The unit the input converts to, or null when it shows the API unit
        function convertTo(): Unit | null {
            if (isCelsiusField($attrs.dtMeasure) && UserPreferencesManager.useFahrenheit) {
                return 'fahrenheit';
            }
            if ($attrs.dtMeasure === 'differentialPressure' && UserPreferencesManager.useInchesOfWater) {
                return 'inchesOfWater';
            }
            return null;
        }

        // Model to input: API unit to the user's unit. Formatters only change what
        // is shown, never the model, so an untouched value is saved exactly as loaded.
        function toUserUnit(value: MeasuredValue): MeasuredValue {
            if (value === null || value === undefined) {
                return value;
            }
            switch (convertTo()) {
                case 'fahrenheit':
                    // Rounded to 1 decimal, so 4.44 °C shows as 40 °F, not 39.992
                    return _round(celsiusToFahrenheit(value), 1);
                case 'inchesOfWater':
                    return _round(pascalToInchesOfWater(value), INCHES_OF_WATER_DECIMALS);
                default:
                    return value;
            }
        }

        // Input to model: the user's unit to API unit. Only runs when the user types.
        function fromUserUnit(value: MeasuredValue): MeasuredValue {
            if (value === null || value === undefined) {
                return value;
            }
            switch (convertTo()) {
                case 'fahrenheit':
                    // Rounded to 2 decimals, so typing 40.1 °F stores 4.5 °C, not 4.5000000000000036
                    return _round(fahrenheitToCelsius(value), 2);
                case 'inchesOfWater':
                    return _round(inchesOfWaterToPascal(value), PASCAL_DECIMALS);
                default:
                    return value;
            }
        }

        ngModel.$formatters.push(toUserUnit);
        ngModel.$parsers.push(fromUserUnit);

        // Formatters only run when the model changes, so redraw the unchanged model when what is measured
        // or the unit shown changes. Watching dt-measure too also covers ngModel formatting a new value
        // before $attrs is updated in the same digest.
        $scope.$watchGroup([() => $attrs.dtMeasure, convertTo], () => ngModel.$processModelValue());
    }

    return {
        restrict: 'A',
        require: 'ngModel',
        link
    };
}

// .ts files are not annotated by Babel's angularjs-annotate, so list the dependencies here
measureDirectiveConfig.$inject = ['UserPreferencesManager'];
